/**
 * Following the playhead in a list (1.9.3): the asset page's Log and Subtitles tabs and the sequence editor's subtitle
 * list. With Follow on, the list's own scroll box moves so that the row at the playhead stays in view as it plays (or
 * after a seek). Scrolling by hand (wheel, touch, a press in the list, PageUp/PageDown/Home/End) stops it until the
 * next play, as the timeline's follow does; nothing moves while something in the list is being typed in.
 */
import { useEffect, useRef, type RefObject } from 'react';

/** The scroll box: its scroll position and visible height (and its content height, for the clamp). */
export interface FollowView { scrollTop: number; clientHeight: number; scrollHeight?: number }
/** A row, measured from the top of the scroll box's content. */
export interface FollowRow { top: number; height: number }

/**
 * Where the scroll box should be so that the row is in view, with `margin` pixels (about one row) of room above it;
 * null when the row is already fully visible, so a row in view never moves the list.
 */
export function followScrollTop(view: FollowView, row: FollowRow, margin: number): number | null {
  const bottom = view.scrollTop + view.clientHeight;
  if (row.top >= view.scrollTop && row.top + row.height <= bottom) return null;
  let to = Math.max(0, row.top - Math.max(0, margin));
  if (view.scrollHeight !== undefined) to = Math.min(to, Math.max(0, view.scrollHeight - view.clientHeight));
  return to === view.scrollTop ? null : to;
}

/** The nearest element that scrolls vertically, from `el` itself upwards. */
export function scrollBoxOf(el: HTMLElement | null): HTMLElement | null {
  for (let n = el; n; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY;
    if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && n.scrollHeight > n.clientHeight) return n;
  }
  return null;
}

const TYPING = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';
const SCROLL_KEYS = new Set(['PageUp', 'PageDown', 'Home', 'End']);

export interface FollowOptions {
  /** The Follow button. */
  enabled: boolean;
  /** Whether the player is playing: a play ends a pause made by scrolling by hand. */
  playing: boolean;
  /** The row to keep in view (null: none at the playhead). */
  targetId: string | null | undefined;
  /** The list whose rows carry the id. */
  list: RefObject<HTMLElement | null>;
  /** The attribute holding the row's id, e.g. 'data-segment' or 'data-cue'. */
  attr: string;
}

/** Keeps the target row in view while enabled (see the file comment). */
export function useFollowPlayhead({ enabled, playing, targetId, list, attr }: FollowOptions): void {
  const manual = useRef(false);
  useEffect(() => { if (playing) manual.current = false; }, [playing]);

  // Scrolling by hand: listened for on the list's scroll box (found again when the list changes size).
  useEffect(() => {
    if (!enabled) return;
    const el = list.current;
    if (!el) return;
    const box = scrollBoxOf(el) ?? el;
    const stop = () => { manual.current = true; };
    const key = (e: KeyboardEvent) => { if (SCROLL_KEYS.has(e.key)) manual.current = true; };
    box.addEventListener('wheel', stop, { passive: true });
    box.addEventListener('touchstart', stop, { passive: true });
    box.addEventListener('pointerdown', stop);
    box.addEventListener('keydown', key);
    return () => {
      box.removeEventListener('wheel', stop);
      box.removeEventListener('touchstart', stop);
      box.removeEventListener('pointerdown', stop);
      box.removeEventListener('keydown', key);
    };
  }, [enabled, list, targetId]);

  useEffect(() => {
    if (!enabled || !targetId || manual.current) return;
    const el = list.current;
    if (!el) return;
    const box = scrollBoxOf(el);
    if (!box) return;
    const active = document.activeElement;
    if (active && box.contains(active) && active.matches(TYPING)) return;
    const row = el.querySelector<HTMLElement>(`[${attr}="${CSS.escape(targetId)}"]`);
    if (!row) return;
    const b = box.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    const top = r.top - b.top - box.clientTop + box.scrollTop;
    const to = followScrollTop({ scrollTop: box.scrollTop, clientHeight: box.clientHeight, scrollHeight: box.scrollHeight }, { top, height: r.height }, r.height);
    if (to !== null) box.scrollTo({ top: to });
    // `playing`: a play after a pause made by hand brings the row back into view at once.
  }, [enabled, targetId, list, attr, playing]);
}
